/**
 * CSP (Content Security Policy) utility functions
 */

interface LaravelWindow {
  Laravel?: {
    csp_nonce?: string;
  };
}

/**
 * Get the CSP nonce from the Laravel window object
 */
export function getCspNonce(): string | undefined {
  return (window as unknown as LaravelWindow).Laravel?.csp_nonce;
}

/**
 * Initialize CSP nonce handling
 */
export function initializeCspNonce(): void {
  const nonce = getCspNonce();
  if (!nonce) return;

  // Store nonce globally for easy access
  (window as any).__cspNonce = nonce;

  // Apply nonce to new elements before they are added to the DOM
  implementNonce(nonce);

}

/**
 * Apply CSP nonce to a specific element (for targeted use only)
 */
export function applyCspNonce(element: Element, nonce?: string): void {
  if (!nonce) {
    nonce = getCspNonce();
  }
  if (!nonce || !(element instanceof HTMLElement)) return;

  element.setAttribute('nonce', nonce);
}

function implementNonce(nonce: string): void {
  // Override document.createElement to intercept style elements
  const originalCreateElement = document.createElement;
  document.createElement = function(tagName: string, options?: ElementCreationOptions): HTMLElement {
    const element = originalCreateElement.call(document, tagName, options);

    if (tagName.toLowerCase() === 'style' && nonce) {
      element.setAttribute('nonce', nonce);
    }

    return element;
  };

  // Override document.createElementNS for SVG style elements
  const originalCreateElementNS = document.createElementNS;
  document.createElementNS = function(namespaceURI: string | null, qualifiedName: string, options?: ElementCreationOptions): Element {
    const element = originalCreateElementNS.call(document, namespaceURI, qualifiedName, options);

    if (qualifiedName.toLowerCase() === 'style' && nonce) {
      element.setAttribute('nonce', nonce);
    }

    return element;
  } as typeof document.createElementNS;

  // Override HTMLElement.prototype.appendChild to catch style elements being added
  const originalAppendChild = HTMLElement.prototype.appendChild;
  HTMLElement.prototype.appendChild = function<T extends Node>(newChild: T): T {
    if (newChild instanceof HTMLStyleElement && nonce && !newChild.getAttribute('nonce')) {
      newChild.setAttribute('nonce', nonce);
    }
    return originalAppendChild.call(this, newChild) as T;
  };

  // Override HTMLElement.prototype.insertBefore to catch style elements being inserted
  const originalInsertBefore = HTMLElement.prototype.insertBefore;
  HTMLElement.prototype.insertBefore = function<T extends Node>(newChild: T, refChild: Node | null): T {
    if (newChild instanceof HTMLStyleElement && nonce && !newChild.getAttribute('nonce')) {
      newChild.setAttribute('nonce', nonce);
    }
    return originalInsertBefore.call(this, newChild, refChild) as T;
  };

  // Apply nonce to dynamically created style elements globally
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLStyleElement && nonce && !node.getAttribute('nonce')) {
          node.setAttribute('nonce', nonce);
        }
      });
    });
  });

  observer.observe(document.head, {
    childList: true,
    subtree: true
  });

  // Also apply nonce to existing style elements
  document.querySelectorAll('style').forEach(style => {
    if (!style.getAttribute('nonce')) {
      style.setAttribute('nonce', nonce);
    }
  });
}
